import { useState, useRef, useEffect, useCallback } from 'react';
import { useTheme } from '../App';
import { FEATURES } from '../constants/features';
import { streamNova, generateSessionKey } from '../api/nova';

const SUGGESTIONS = [
  'Is ₹80L fair for Whitefield 2BHK?',
  'Best localities under ₹70L near Manyata?',
  'How do I know if a property is legally safe?',
];

const prettyTool = (name) =>
  (name || '').replace(/_/g, ' ').replace(/\bnova\b/gi, '').trim();

export default function NovaWidget() {
  // FEATURE GATE — NOVA is dormant until REACT_APP_FEAT_NOVA === 'true'
  if (!FEATURES.NOVA) return null;
  return <NovaWidgetInner />;
}

function NovaWidgetInner() {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]); // { role: 'user'|'assistant', content }
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [activeTool, setActiveTool] = useState(null);

  const sessionKeyRef = useRef(null);
  const messagesEndRef = useRef(null);

  // SESSION KEY — read from sessionStorage on mount, generate + persist if absent.
  if (sessionKeyRef.current === null) {
    sessionKeyRef.current = generateSessionKey();
  }

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, activeTool, scrollToBottom]);

  // ─── Design tokens ───────────────────────────────────────────────
  const accent = isLight ? '#007A8A' : '#00E5FF';
  const onAccent = isLight ? '#ffffff' : '#080d16';
  const cardBg = isLight ? '#FFFFFF' : 'rgba(10,15,26,0.95)';
  const cardBorder = isLight ? 'rgba(0,189,204,0.22)' : 'rgba(255,255,255,0.08)';
  const textPrimary = isLight ? '#0f1720' : '#e6edf3';
  const textMuted = isLight ? '#5a6b78' : '#7c8b99';
  const userBubbleText = onAccent;
  const sora = "'Sora', sans-serif";
  const manrope = "'Manrope', sans-serif";

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;

    setInput('');
    setActiveTool(null);

    // History sent to the backend = prior turns + this new user message.
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    const payload = [...history, { role: 'user', content }];

    setMessages((prev) => [
      ...prev,
      { role: 'user', content },
      { role: 'assistant', content: '' },
    ]);
    setStreaming(true);

    const appendToAssistant = (chunk) => {
      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        updated[updated.length - 1] = { role: 'assistant', content: (last?.content || '') + chunk };
        return updated;
      });
    };

    const setAssistant = (value) => {
      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: value };
        return updated;
      });
    };

    await streamNova({
      messages: payload,
      sessionKey: sessionKeyRef.current,
      onToken: (t) => appendToAssistant(t),
      onToolUse: (name) => setActiveTool(name),
      onToolResult: () => setActiveTool(null),
      onDone: () => {
        setStreaming(false);
        setActiveTool(null);
      },
      onError: (msg) => {
        setStreaming(false);
        setActiveTool(null);
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (!last?.content) {
            updated[updated.length - 1] = {
              role: 'assistant',
              content: "I hit a snag reaching the data just now. Give it another try in a moment.",
            };
          }
          return updated;
        });
        // eslint-disable-next-line no-console
        console.error('[NOVA]', msg);
      },
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const showingTyping =
    streaming &&
    !activeTool &&
    messages.length > 0 &&
    messages[messages.length - 1]?.role === 'assistant' &&
    messages[messages.length - 1]?.content === '';

  // ─── Closed pill ─────────────────────────────────────────────────
  if (!isOpen) {
    return (
      <>
        <style>{novaKeyframes}</style>
        <button
          onClick={() => setIsOpen(true)}
          aria-label="Ask NOVA"
          style={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            zIndex: 60,
            height: 44,
            padding: '0 16px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            border: 'none',
            borderRadius: 50,
            cursor: 'pointer',
            background: accent,
            color: onAccent,
            fontFamily: sora,
            fontSize: 13,
            fontWeight: 600,
            animation: 'novaPulse 3s ease-in-out infinite',
            transition: 'all 0.2s ease',
          }}
        >
          <span style={{ fontSize: 14, lineHeight: 1 }}>✦</span>
          Ask NOVA
        </button>
      </>
    );
  }

  // ─── Open card ───────────────────────────────────────────────────
  return (
    <>
      <style>{novaKeyframes}</style>
      <div
        style={{
          position: 'fixed',
          bottom: 80,
          right: 16,
          zIndex: 60,
          width: 340,
          maxWidth: 'calc(100vw - 32px)',
          height: 480,
          maxHeight: 'calc(100vh - 120px)',
          display: 'flex',
          flexDirection: 'column',
          background: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: 18,
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          boxShadow: isLight
            ? '0 20px 60px rgba(0,122,138,0.18)'
            : '0 20px 60px rgba(0,0,0,0.55)',
          overflow: 'hidden',
          transition: 'all 0.2s ease',
        }}
      >
        {/* HEADER */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '14px 16px',
            borderBottom: `1px solid ${cardBorder}`,
          }}
        >
          <div>
            <div style={{ fontFamily: sora, fontWeight: 700, fontSize: 15, color: accent }}>
              ✦ NOVA
            </div>
            <div style={{ fontFamily: manrope, fontSize: 11, color: textMuted, marginTop: 2 }}>
              Bangalore property intelligence
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            aria-label="Close NOVA"
            style={{
              border: 'none',
              background: 'transparent',
              color: textMuted,
              fontSize: 20,
              lineHeight: 1,
              cursor: 'pointer',
              padding: 2,
              transition: 'all 0.2s ease',
            }}
          >
            ×
          </button>
        </div>

        {/* MESSAGES */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: '14px 16px',
          }}
        >
          {messages.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontFamily: manrope, fontSize: 13, color: textMuted, lineHeight: 1.6 }}>
                Ask me anything about Bangalore property — pricing, localities, or whether a
                deal is fair.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    style={{
                      textAlign: 'left',
                      border: `1px solid ${cardBorder}`,
                      background: 'transparent',
                      color: textPrimary,
                      fontFamily: manrope,
                      fontSize: 13,
                      borderRadius: 50,
                      padding: '9px 14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) =>
              m.role === 'user' ? (
                <div key={i} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <div
                    style={{
                      maxWidth: '82%',
                      background: accent,
                      color: userBubbleText,
                      fontFamily: manrope,
                      fontSize: 13,
                      lineHeight: 1.55,
                      borderRadius: 50,
                      padding: '8px 14px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {m.content}
                  </div>
                </div>
              ) : (
                <div key={i} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <div
                    style={{
                      maxWidth: '100%',
                      color: textPrimary,
                      fontFamily: manrope,
                      fontSize: 13,
                      lineHeight: 1.65,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {m.content}
                  </div>
                </div>
              )
            )
          )}

          {/* TOOL USE indicator */}
          {activeTool && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div
                style={{
                  fontFamily: manrope,
                  fontSize: 12,
                  color: textMuted,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span style={{ animation: 'novaBlink 1s step-end infinite' }}>⚡</span>
                checking {prettyTool(activeTool)}…
              </div>
            </div>
          )}

          {/* TYPING indicator (3 dots) */}
          {showingTyping && (
            <div style={{ display: 'flex', gap: 5, paddingTop: 2 }}>
              {[0, 1, 2].map((d) => (
                <span
                  key={d}
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: accent,
                    display: 'inline-block',
                    animation: 'novaBounce 1.2s infinite',
                    animationDelay: `${d * 0.18}s`,
                  }}
                />
              ))}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* INPUT */}
        <div
          style={{
            padding: '12px 14px',
            borderTop: `1px solid ${cardBorder}`,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 8,
          }}
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={streaming}
            placeholder="Ask NOVA anything about Bangalore property..."
            style={{
              flex: 1,
              resize: 'none',
              maxHeight: 72, // ~3 rows
              border: `1px solid ${cardBorder}`,
              borderRadius: 14,
              background: isLight ? '#EEF6F8' : 'rgba(255,255,255,0.04)',
              color: textPrimary,
              fontFamily: manrope,
              fontSize: 14,
              lineHeight: 1.4,
              padding: '9px 12px',
              outline: 'none',
            }}
          />
          <button
            onClick={() => send()}
            disabled={!input.trim() || streaming}
            aria-label="Send"
            style={{
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: 50,
              border: 'none',
              cursor: input.trim() && !streaming ? 'pointer' : 'not-allowed',
              background: input.trim() && !streaming ? accent : (isLight ? '#cdd9dd' : '#243040'),
              color: input.trim() && !streaming ? onAccent : (isLight ? '#8b9aa1' : '#5a6b78'),
              fontSize: 18,
              fontWeight: 700,
              transition: 'all 0.2s ease',
            }}
          >
            →
          </button>
        </div>
      </div>
    </>
  );
}

const novaKeyframes = `
@keyframes novaPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(0,229,255,0.35); }
  50%      { box-shadow: 0 0 0 8px rgba(0,229,255,0); }
}
@keyframes novaBounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
  40%           { transform: translateY(-4px); opacity: 1; }
}
@keyframes novaBlink {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.3; }
}
`;
